import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ═════════════════════════════════════════════════════════════════
// TOKEN REFRESH INTERCEPTOR
// Automatically refreshes expired access tokens using httpOnly cookie
// ═════════════════════════════════════════════════════════════════

let _isRefreshing = false;
let _refreshQueue = [];
let _sessionExpiredShown = false;

const processQueue = (error, token = null) => {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _refreshQueue = [];
};

// Reset session expired flag on successful login
export function resetSessionExpiredFlag() {
  _sessionExpiredShown = false;
}

axios.defaults.withCredentials = true;

// v1.0.7 — Hydrate Authorization header from localStorage on module load.
// Fixes the "You've been signed out" flash toast that fired on every page
// reload: without this, initial API calls went out with no Authorization
// header, got 401, the interceptor then tried /auth/refresh (which 401s in
// preview due to cross-origin httpOnly cookie behavior), and line ~123
// wiped the still-valid token from localStorage. Hydrating on boot means
// the first API calls already carry the token, no refresh is attempted,
// and the toast never fires.
try {
  const _storedToken = typeof window !== "undefined" ? window.localStorage?.getItem("token") : null;
  if (_storedToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${_storedToken}`;
  }
} catch {}

// ═════════════════════════════════════════════════════════════════
// GLOBAL AXIOS INTERCEPTOR — Error Brain Part 9
// Maps HTTP error codes to ErrorToast message types.
// Never exposes raw HTTP status codes to users.
// ═════════════════════════════════════════════════════════════════

let _showErrorFn = null;
export function setErrorBrainHook(showError) {
  _showErrorFn = showError;
}

// ═════════════════════════════════════════════════════════════════
// v1.0.0 GATE 3 — PostHog points_earned / badge_earned tracking
// Scans every API response for points_awarded / points_earned fields
// and fires corresponding PostHog events. Uses lazy import to avoid
// circular dependency with @/lib/events.
// ═════════════════════════════════════════════════════════════════
axios.interceptors.response.use(
  (response) => {
    try {
      const data = response?.data;
      if (data && typeof data === "object") {
        const pts = data.points_awarded ?? data.points_earned;
        if (typeof pts === "number" && pts > 0) {
          import("./lib/events").then(({ trackPointsEarned }) => {
            trackPointsEarned(
              data.rule_id || response.config?.url?.replace(/^.*\/api\//, "") || "unknown",
              pts,
              data.trigger_event || data.event_type || "api_response",
              data.total_points ?? data.total_points_after ?? null
            );
          }).catch(() => {});
        }
        const badges = data.new_badges || data.badges_earned || data.badges_awarded;
        if (Array.isArray(badges) && badges.length > 0) {
          import("./lib/events").then(({ trackBadgeEarned }) => {
            badges.forEach((b) => {
              const id = typeof b === "string" ? b : (b?.badge_id || b?.id || "unknown");
              const name = typeof b === "string" ? b : (b?.name || b?.badge_name || id);
              trackBadgeEarned(id, name, data.trigger_event || "api_response");
            });
          }).catch(() => {});
          // v1.0.0 Gate 5.5 — Celebratory toast for each earned badge
          import("sonner").then(({ toast }) => {
            badges.forEach((b) => {
              const id = typeof b === "string" ? b : (b?.badge_id || b?.id || "unknown");
              const name = typeof b === "string" ? b : (b?.name || b?.badge_name || id);
              const desc = typeof b === "object" ? (b?.badge_description || b?.description || "") : "";
              const icon = typeof b === "object" ? (b?.badge_icon || b?.icon || "award") : "award";
              // Persist firstBadgeShown so the very first badge earned gets the richer animation
              const firstKey = "first_badge_shown";
              const isFirst = typeof window !== "undefined" && !window.localStorage?.getItem(firstKey);
              if (isFirst) {
                try { window.localStorage.setItem(firstKey, "1"); } catch {}
                window.dispatchEvent(new CustomEvent("first-badge-earned", {
                  detail: { badge_id: id, name, description: desc, icon },
                }));
              }
              toast.success(`Badge earned — ${name}`, { duration: 4500 });
            });
          }).catch(() => {});
        }
      }
    } catch {}
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Auto-refresh on 401 (token expired) — except for auth endpoints
    if (status === 401 && !originalRequest._retry && !originalRequest.url?.includes("/auth/login") && !originalRequest.url?.includes("/auth/register") && !originalRequest.url?.includes("/auth/refresh")) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }
      originalRequest._retry = true;
      _isRefreshing = true;
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const newToken = data.access_token;
        localStorage.setItem("token", newToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // v1.0.7 — Only clear auth state + show SESSION_EXPIRED toast if we
        // have clear evidence the session is actually dead. In the preview
        // environment the /auth/refresh call can 401 purely because the
        // httpOnly refresh cookie is cross-origin blocked — even when the
        // user's access token is still perfectly valid. In that case the
        // user is NOT signed out; wiping the token + flashing the toast
        // creates the exact glitch the founder reported. Defensive rule:
        // if the original 401 was on a request that never actually carried
        // an Authorization header, leave the token alone and stay silent —
        // the next request (which now carries the header, thanks to
        // hydrate-on-boot) will succeed.
        const originalHadAuth = !!originalRequest?.headers?.Authorization ||
          !!originalRequest?.headers?.authorization;
        if (originalHadAuth) {
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
          if (_showErrorFn && !_sessionExpiredShown) {
            _sessionExpiredShown = true;
            _showErrorFn("SESSION_EXPIRED", {});
          }
        }
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    if (!_showErrorFn) return Promise.reject(error);

    const fingerprint = error.response?.data?.fingerprint || "";
    const endpoint = error.config?.url || "";

    // Skip auth-related 401s on login page (handled by auth flow)
    if (status === 401 && endpoint.includes("/auth/")) {
      return Promise.reject(error);
    }

    // Skip 401s after session already expired (prevents blinking modal loop)
    if (status === 401 && _sessionExpiredShown) {
      return Promise.reject(error);
    }

    if (!error.response && !navigator.onLine) {
      _showErrorFn("OFFLINE", { fingerprint, endpoint });
    } else if (!error.response) {
      _showErrorFn("NETWORK_SLOW", { fingerprint, endpoint });
    } else if (status === 401) {
      _sessionExpiredShown = true;
      _showErrorFn("SESSION_EXPIRED", { fingerprint, endpoint });
    } else if (status === 429) {
      _showErrorFn("NETWORK_SLOW", {
        fingerprint, endpoint,
        message: "We're getting a lot of requests right now. Try again in a moment.",
      });
    } else if (status === 502 || status === 503) {
      _showErrorFn("DB_TIMEOUT", { fingerprint, endpoint });
    } else if (status >= 500) {
      _showErrorFn("UNKNOWN_ERROR", { fingerprint, endpoint });
    }

    return Promise.reject(error);
  }
);

// US Date formatter (MM/DD/YYYY)
export const formatDateUS = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${m}/${d}/${y}`;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

// Military time formatter (HH:MM)
export const formatMilitaryTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export { axios };

// Open external URL — falls back to clipboard copy if popup blocked
export const openExternal = (url, e) => {
  if (e) e.preventDefault();
  if (e) e.stopPropagation();
  const w = window.open(url, "_blank");
  if (!w || w.closed) {
    navigator.clipboard.writeText(url).catch(() => {});
    // Return false to signal the caller to show their own toast
    return false;
  }
  return true;
};
